import { createFileRoute } from '@tanstack/react-router'
import { scoreColor } from '@workspace/seedforge-shared'
import { CtaBanner } from '@workspace/seedforge-ui/components/cta-banner'
import {
  EvaluationActions,
  EvaluationFeedback,
  EvaluationHeader,
  EvaluationInvestmentThesis,
  EvaluationMetricBreakdown,
  EvaluationMetricScores,
  EvaluationRisks,
  EvaluationSummary,
  EvaluationTensions,
  PitchEvaluationView,
} from '@workspace/seedforge-ui/evaluation'
import { Avatar, AvatarFallback } from '@workspace/ui/components/avatar'
import { cn } from '@workspace/ui/lib/utils'
import { Loader2, MessageCircleQuestion, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { ConnectDialog } from '../../../../components/connect-dialog'
import { InvestorLayout } from '../../../../components/investor-layout'
import { useInvestorConnectMutation, useInvestorEvaluationQuery } from '../../../../hooks/api'
import { useBasePath } from '../../../../hooks/use-base-path'

export const Route = createFileRoute('/app/investor/$id/evaluation/$evaluationId')({
  component: InvestorEvaluationPage,
})

function InvestorEvaluationPage() {
  const { id: investorId, evaluationId } = Route.useParams()
  const basePath = useBasePath()
  const { data, isLoading, error } = useInvestorEvaluationQuery(investorId, evaluationId)
  const connectMutation = useInvestorConnectMutation()
  const [connectOpen, setConnectOpen] = useState(false)

  if (isLoading) {
    return (
      <InvestorLayout activeNav="Feed">
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </InvestorLayout>
    )
  }

  if (error || !data?.data) {
    return (
      <InvestorLayout activeNav="Feed">
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          Failed to load evaluation
        </div>
      </InvestorLayout>
    )
  }

  const evaluation = data.data
  const isSingleJudge = evaluation.judges.length <= 1
  const judge = evaluation.judges[0]
  // Custom evaluations (single-judge, investor perspective) show the same title as in the feed
  const customTitle = isSingleJudge
    ? `${evaluation.startupName}: Custom Evaluation`
    : evaluation.title

  const dateStr = new Date(evaluation.date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  const handleConnect = async (message?: string) => {
    await connectMutation.mutateAsync({
      path: { investorId, evaluationId },
      body: { message: message ?? '' },
    })
    setConnectOpen(false)
  }

  const connectDialog = (
    <ConnectDialog
      startupName={evaluation.startupName}
      open={connectOpen}
      onOpenChange={setConnectOpen}
      onConnect={handleConnect}
      isPending={connectMutation.isPending}
    />
  )

  // Multi-judge evaluations — use the standard view
  if (!isSingleJudge) {
    return (
      <InvestorLayout activeNav="Feed">
        <PitchEvaluationView
          title={evaluation.title}
          sessionLabel={evaluation.sessionLabel}
          date={dateStr}
          pitchDuration={evaluation.pitchDuration}
          summary={evaluation.summary}
          score={evaluation.score}
          metrics={evaluation.metrics.map((m) => ({ ...m, delta: m.delta ?? undefined }))}
          winnerJudge={evaluation.winnerJudge}
          judges={evaluation.judges}
          metricBreakdowns={evaluation.metricBreakdowns}
          tensions={evaluation.tensions}
          risks={evaluation.risks}
          actions={evaluation.actions}
          improvements={evaluation.improvements}
          judgeDetailPath={(judgeKey: string) => `${basePath}/judge/${judgeKey}/${evaluationId}`}
          homePath={`${basePath}/feed`}
          pitchPath={`${basePath}/feed`}
          isPrescreen={evaluation.isPrescreen}
          variant="investor"
          onConnect={() => setConnectOpen(true)}
        />
        {connectDialog}
      </InvestorLayout>
    )
  }

  // Single-judge custom evaluation — custom composed view
  return (
    <InvestorLayout activeNav="Feed">
      <div className="mx-auto max-w-6xl py-2">
        {/* Header */}
        <EvaluationHeader
          title={customTitle}
          sessionLabel={evaluation.sessionLabel}
          date={dateStr}
          pitchDuration={evaluation.pitchDuration}
          score={evaluation.score}
        />

        {/* Metric scores */}
        {evaluation.metrics.length > 0 && (
          <div className="mt-5">
            <EvaluationMetricScores
              metrics={evaluation.metrics.map((m) => ({ ...m, delta: m.delta ?? undefined }))}
              hideMedia
              compact
            />
          </div>
        )}

        {/* Summary */}
        <EvaluationSummary summary={evaluation.summary} />

        {/* Persona analysis card — inline judge data */}
        {judge && (
          <div className="mt-6 rounded-xl border border-border bg-card/30 p-6">
            <div className="flex items-center gap-3">
              <Avatar className="size-10">
                <AvatarFallback
                  className="text-xs font-bold text-white"
                  style={{ backgroundColor: `${judge.color}20` }}
                >
                  <span style={{ color: judge.color }}>{judge.initials}</span>
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{judge.persona}</p>
                <p
                  className={cn(
                    'text-[10px] font-medium',
                    judge.verdictType === 'positive' ? 'text-emerald-400' : 'text-muted-foreground'
                  )}
                >
                  {judge.verdict}
                </p>
              </div>
              <div className="text-right">
                <p className={cn('text-lg font-bold tabular-nums', scoreColor(judge.avgScore))}>
                  {judge.avgScore}
                </p>
                <p className="text-[9px] text-muted-foreground/80">avg score</p>
              </div>
            </div>

            {/* Takeaway */}
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{judge.takeaway}</p>

            {/* Strength + Concern */}
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-emerald-500/5 px-4 py-3">
                <p className="text-[10px] font-medium uppercase tracking-wider text-emerald-400/80">
                  Top Strength
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {judge.topStrength}
                </p>
              </div>
              <div className="rounded-lg bg-red-500/5 px-4 py-3">
                <p className="text-[10px] font-medium uppercase tracking-wider text-red-400/80">
                  Top Concern
                </p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {judge.topConcern}
                </p>
              </div>
            </div>

            {/* Quote */}
            {judge.quote && (
              <p className="mt-4 border-t border-border/50 pt-4 text-xs leading-relaxed text-muted-foreground/80 italic">
                &ldquo;{judge.quote}&rdquo;
              </p>
            )}
          </div>
        )}

        {/* Metric breakdowns */}
        {evaluation.metricBreakdowns.length > 0 && (
          <div className="mt-6">
            <div className="mb-3 flex items-center gap-3">
              <h2 className="text-sm font-semibold text-foreground">Metric Deep-Dive</h2>
              <span className="text-[10px] text-muted-foreground/80">
                Strengths · gaps · per metric
              </span>
            </div>
            <div className="space-y-3">
              {evaluation.metricBreakdowns.map((m) => (
                <EvaluationMetricBreakdown key={m.key} metric={{ ...m, judgeScores: [] }} />
              ))}
            </div>
          </div>
        )}

        {/* Investment thesis */}
        {evaluation.winnerJudge && (
          <EvaluationInvestmentThesis winnerJudge={evaluation.winnerJudge} />
        )}

        {/* Core tensions */}
        {evaluation.tensions.length > 0 && <EvaluationTensions tensions={evaluation.tensions} />}

        {/* Risks */}
        {evaluation.risks.length > 0 && <EvaluationRisks risks={evaluation.risks} />}

        {/* Open questions */}
        {evaluation.openQuestions && evaluation.openQuestions.length > 0 && (
          <div className="mt-8 rounded-xl border border-border bg-card/30 px-6 py-5">
            <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground/80">
              Key Questions
            </h2>
            <div className="space-y-3">
              {evaluation.openQuestions.map((q) => (
                <div key={q.title} className="flex items-start gap-3">
                  <MessageCircleQuestion className="mt-0.5 size-4 shrink-0 text-amber-400" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{q.title}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground/70">
                      {q.detail}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actionable next steps */}
        {evaluation.actions.length > 0 && <EvaluationActions actions={evaluation.actions} />}

        {/* Feedback */}
        <EvaluationFeedback className="mt-8" />

        {/* Connect CTA */}
        <section className="mt-6">
          <CtaBanner
            icon={<MessageSquare className="size-5 text-[#f97316]" />}
            title="Interested in this startup?"
            description="Reach out to the founder directly"
            actionLabel="Connect with Startup"
            onAction={() => setConnectOpen(true)}
            variant="primary"
          />
        </section>
      </div>

      {connectDialog}
    </InvestorLayout>
  )
}
